import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from '../../components/toasts';
import { Button, ErrorText, Input, Spinner, Textarea } from '../../components/ui';
import { errorMessage, get, put } from '../../lib/api';
import { dateTimeOf } from '../../lib/format';
import { SettingItem } from '../../lib/types';

/** Configurações da plataforma. Valores em JSON: texto entre aspas, números e true/false sem aspas. */
export function SettingsAdmin() {
  const { data, isLoading, error } = useQuery({ queryKey: ['settings-admin'], queryFn: () => get<SettingItem[]>('/settings') });
  if (isLoading) return <Spinner />;
  return (
    <div>
      <p className="mb-3 text-sm text-fg-soft">Cada alteração fica registrada na auditoria. Textos aceitam marcadores como {'{operator}'}, {'{code}'} e {'{link}'}, conforme a descrição.</p>
      <ErrorText error={error} />
      <div className="divide-y divide-border rounded-lg border border-border bg-panel">
        {data?.map((s) => <SettingRow key={s.key} item={s} />)}
      </div>
    </div>
  );
}

function SettingRow({ item }: { item: SettingItem }) {
  const qc = useQueryClient();
  const initial = typeof item.value === 'string' ? item.value : JSON.stringify(item.value);
  const isText = typeof item.value === 'string';
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const dirty = value !== initial;

  const save = async () => {
    let parsed: unknown = value;
    if (!isText) {
      try {
        parsed = JSON.parse(value);
      } catch {
        // Não é número, true/false, null nem JSON: grava como texto (ex.: um link numa chave hoje vazia).
        parsed = value;
      }
    }
    setSaving(true);
    try {
      await put(`/settings/${encodeURIComponent(item.key)}`, { value: parsed });
      toast('Configuração salva', 'ok');
      void qc.invalidateQueries({ queryKey: ['settings-admin'] });
      void qc.invalidateQueries({ queryKey: ['settings'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto] items-start gap-3 px-4 py-3">
      <div>
        <div className="font-mono text-xs">{item.key}</div>
        {item.description && <div className="mt-0.5 text-xs text-fg-soft">{item.description}</div>}
        <div className="mt-0.5 text-[11px] text-fg-soft">alterado em {dateTimeOf(item.updatedAt)}</div>
      </div>
      {isText ? (
        // Texto: várias linhas, mantendo as quebras (ex.: menu do robô). Cresce com o conteúdo até um limite.
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={Math.min(8, value.split('\n').length)}
          className="max-h-60 min-h-9 font-mono text-xs [field-sizing:content]"
        />
      ) : (
        <Input value={value} onChange={(e) => setValue(e.target.value)} className="font-mono text-xs" />
      )}
      <Button size="sm" variant={dirty ? 'primary' : 'secondary'} disabled={!dirty} loading={saving} onClick={() => void save()}>
        Salvar
      </Button>
    </div>
  );
}
